import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Separator } from '../../ui/separator';
import { ScrollArea } from '../../ui/scroll-area';
import {
  ChevronLeft,
  ChevronRight,
  Save,
  Send,
  FileText,
  Package,
  DollarSign,
  Paperclip,
  CheckCircle,
  AlertTriangle,
  Trash2,
  Plus,
  Building2,
  User,
  Calendar,
  Truck
} from 'lucide-react';
import { ItemSearchSelect } from './ItemSearchSelect';
import { AddCustomItemModal } from './AddCustomItemModal';
import { procurementService, ProcurementItem, Vendor, PurchaseOrder } from '../../../src/services/procurement.service';
import { showSuccess, showError, showWarning } from '../../../src/utils/notification.utils';
import { authService } from '../../../src/services/auth.service';
import { projectService, Project } from '../../../src/services/project.service';
import { userService, UserProfile } from '../../../src/services/user.service';
import { cn } from '../../ui/utils';

interface PurchaseOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit' | 'duplicate';
  orderId?: string;
  duplicateData?: PurchaseOrder;
  onSuccess?: (order: PurchaseOrder) => void;
}

interface POLineItem {
  temp_id: string;
  procurement_item_id?: string;
  item_code: string;
  item_name: string;
  description?: string;
  quantity_ordered: number;
  unit_of_measure: string;
  unit_price: number;
  discount_percentage?: number;
  discount_amount?: number;
  tax_rate?: number;
  tax_amount?: number;
  line_total: number;
  cost_code_id?: string;
  required_date?: string;
  manufacturer?: string;
  model_number?: string;
  specifications?: any;
  line_notes?: string;
}

interface POFormData {
  // Step 1: Basic Information
  po_number?: string;
  vendor_id: string;
  project_id: string;
  requested_by: string;
  department?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  po_date: string;
  requested_delivery_date: string;
  requisition_number?: string;

  // Step 2: Delivery & Shipping
  delivery_street?: string;
  delivery_city?: string;
  delivery_state?: string;
  delivery_zip?: string;
  delivery_country?: string;
  shipping_method?: 'ground' | 'express' | 'overnight' | 'two_day' | 'freight' | 'ocean' | 'air' | 'rail' | 'courier' | 'pickup' | 'delivery';
  freight_terms?: string;
  partial_delivery: boolean; // Changed from allow_partial_delivery to match backend
  delivery_instructions?: string;
  vendor_contact_person?: string;
  vendor_contact_email?: string;
  vendor_contact_phone?: string;

  // Step 3: Line Items
  items: POLineItem[];

  // Step 4: Financial Details
  subtotal: number;
  discount_percentage?: number;
  discount_amount?: number;
  tax_rate?: number;
  tax_amount?: number;
  freight_amount?: number;
  other_charges?: number;
  total_amount: number;
  payment_terms?: string;
  payment_method?: string;

  // Step 5: Terms & Attachments
  warranty_terms?: string;
  retention_percentage?: number;
  retention_amount?: number;
  terms_and_conditions?: string;
  special_instructions?: string;
  attachment_ids?: string[];
}

const STEPS = [
  { id: 1, title: 'Basic Info', icon: FileText },
  { id: 2, title: 'Delivery', icon: Truck },
  { id: 3, title: 'Line Items', icon: Package },
  { id: 4, title: 'Financial', icon: DollarSign },
  { id: 5, title: 'Terms', icon: Paperclip },
  { id: 6, title: 'Review', icon: CheckCircle },
];

export function PurchaseOrderModal({ open, onOpenChange, mode, orderId, duplicateData, onSuccess }: PurchaseOrderModalProps): JSX.Element {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [vendorContacts, setVendorContacts] = useState<any[]>([]);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<ProcurementItem | null>(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [budgetWarnings, setBudgetWarnings] = useState<string[]>([]);

  // Helper to safely format currency
  const formatCurrency = (value: number | string | undefined): string => {
    if (value === undefined || value === null) return '0.00';
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const [formData, setFormData] = useState<POFormData>({
    vendor_id: '',
    project_id: '',
    requested_by: '',
    department: '',
    priority: 'normal',
    po_date: new Date().toISOString().split('T')[0],
    requested_delivery_date: '',
    requisition_number: '',
    delivery_street: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    delivery_country: 'USA',
    shipping_method: 'ground',
    freight_terms: 'FOB Destination',
    partial_delivery: true, // Changed from allow_partial_delivery
    delivery_instructions: '',
    vendor_contact_person: '',
    vendor_contact_email: '',
    vendor_contact_phone: '',
    items: [],
    subtotal: 0,
    discount_percentage: 0,
    discount_amount: 0,
    tax_rate: 0,
    tax_amount: 0,
    freight_amount: 0,
    other_charges: 0,
    total_amount: 0,
    payment_terms: 'net_30',
    payment_method: '',
    warranty_terms: '',
    retention_percentage: 0,
    retention_amount: 0,
    terms_and_conditions: '',
    special_instructions: '',
    attachment_ids: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load vendors and projects on mount
  useEffect(() => {
    if (open) {
      loadInitialData();
    }
  }, [open]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const userProfile = await authService.getUserProfile();

      // Load vendors from DB
      const vendorsResponse = await procurementService.getVendors(1, 100, {
        organization_id: userProfile.organizationId
      });
      console.log('Vendors response:', vendorsResponse);
      if (vendorsResponse.success && vendorsResponse.data) {
        const vendorList = vendorsResponse.data.data || vendorsResponse.data || [];
        console.log('Raw vendor list:', vendorList);
        // Filter only active vendors
        const activeVendors = Array.isArray(vendorList) ? vendorList.filter(v => v.status === 'active') : [];
        console.log('Active vendors:', activeVendors);
        setVendors(activeVendors);
      }

      // Load projects from DB
      const projectsResponse = await projectService.getProjects(1, 100, {
        organization_id: userProfile.organizationId
      });
      console.log('Projects response:', projectsResponse);
      if (projectsResponse.success && projectsResponse.data) {
        const projectList = projectsResponse.data.data || projectsResponse.data || [];
        console.log('Raw project list:', projectList);
        // Filter only active projects
        const activeProjects = Array.isArray(projectList) ? projectList.filter(p => p.status === 'active' || p.status === 'planning') : [];
        console.log('Active projects:', activeProjects);
        setProjects(activeProjects);
      }

      // Load staff from DB
      const staffResponse = await userService.getUsers(1, 100, {
        organization_id: userProfile.organizationId,
        is_active: true
      });
      console.log('Staff response:', staffResponse);
      if (staffResponse.success && staffResponse.data) {
        const staffList = Array.isArray(staffResponse.data) ? staffResponse.data : [];
        console.log('Staff list:', staffList);
        setStaff(staffList);
      }

      // Get next PO number for new orders and duplicates
      if (mode === 'create' || mode === 'duplicate') {
        const poNumberResponse = await procurementService.getNextPONumber(userProfile.organizationId);
        console.log('PO Number response:', poNumberResponse);
        if (poNumberResponse.success && poNumberResponse.data) {
          setFormData(prev => ({
            ...prev,
            po_number: poNumberResponse.data.po_number,
            requested_by: userProfile.id
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            requested_by: userProfile.id
          }));
        }
      } else {
        // Set default requested_by for edit mode
        setFormData(prev => ({
          ...prev,
          requested_by: userProfile.id
        }));
      }

      // Load existing PO if editing
      if (mode === 'edit' && orderId) {
        await loadExistingPO(orderId);
      }

      // Load duplicate data if duplicating
      if (mode === 'duplicate' && duplicateData) {
        loadDuplicateData(duplicateData);
      }
    } catch (error: any) {
      console.error('Load initial data error:', error);
      showError({
        title: 'Load Failed',
        message: error?.message || 'Failed to load initial data'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadExistingPO = async (poId: string) => {
    try {
      setLoading(true);
      const response = await procurementService.getPurchaseOrder(poId);
      console.log('[PurchaseOrderModal] Load existing PO response:', response);

      if (response.success && response.data) {
        // Handle nested data structure
        const po = (response.data as any).data || response.data;
        console.log('[PurchaseOrderModal] Extracted PO data:', po);

        // Fetch items separately if not included
        let items = po.items || [];
        if (!items || items.length === 0) {
          try {
            const itemsResponse = await procurementService.getPurchaseOrderItems(poId);
            const itemsData = (itemsResponse.data as any).data || itemsResponse.data;
            items = Array.isArray(itemsData) ? itemsData : [];
          } catch (error) {
            console.error('[PurchaseOrderModal] Failed to fetch items:', error);
          }
        }

        // Map items to form structure - use item_code and description like create form
        const formItems: POLineItem[] = items.map((item: any, idx: number) => ({
          temp_id: `temp_${Date.now()}_${idx}`,
          procurement_item_id: item.item_id,
          item_code: item.item_code,
          item_name: item.item_code, // Use item_code as item_name for display consistency
          description: item.description || item.item_name, // Use description or fall back to item_name
          quantity_ordered: item.quantity_ordered,
          unit_of_measure: item.unit_of_measure,
          unit_price: Number(item.unit_price || 0),
          discount_percentage: Number((item as any).discount_percentage || 0),
          discount_amount: Number((item as any).discount_amount || 0),
          tax_rate: Number((item as any).tax_rate || 0),
          tax_amount: Number((item as any).tax_amount || 0),
          line_total: Number(item.total_amount || 0) > 0
            ? Number(item.total_amount)
            : Number(item.quantity_ordered || 0) * Number(item.unit_price || 0),
          cost_code_id: (item as any).cost_code_id,
          required_date: item.requested_delivery_date,
          manufacturer: (item as any).manufacturer,
          model_number: (item as any).model_number,
          specifications: (item as any).specifications,
          line_notes: (item as any).line_notes || ''
        }));

        console.log('[PurchaseOrderModal] Mapped form items:', formItems);

        // Map all PO fields to form data - use correct field mappings
        setFormData({
          po_number: po.po_number,
          vendor_id: po.vendor_id,
          project_id: po.project_id || '',
          requested_by: po.created_by || '',
          department: (po as any).department || '',
          priority: (po as any).priority || 'normal',
          po_date: po.po_date ? po.po_date.split('T')[0] : new Date().toISOString().split('T')[0],
          requested_delivery_date: po.requested_delivery_date || po.required_date || '',
          requisition_number: (po as any).requisition_number || '',
          // Parse ship_to_address JSON structure
          delivery_street: (po as any).ship_to_address?.street || '',
          delivery_city: (po as any).ship_to_address?.city || '',
          delivery_state: (po as any).ship_to_address?.state || '',
          delivery_zip: (po as any).ship_to_address?.zip || '',
          delivery_country: (po as any).ship_to_address?.country || 'USA',
          shipping_method: po.shipping_method || 'ground',
          freight_terms: (po as any).shipping_terms || (po as any).freight_terms || 'FOB Destination', // Use shipping_terms
          partial_delivery: (po as any).partial_delivery !== undefined ? (po as any).partial_delivery : true, // Use partial_delivery
          delivery_instructions: (po as any).delivery_instructions || '',
          vendor_contact_person: (po as any).vendor_contact_person || '',
          vendor_contact_email: (po as any).vendor_contact_email || '',
          vendor_contact_phone: (po as any).vendor_contact_phone || '',
          items: formItems,
          subtotal: Number(po.subtotal || 0),
          discount_percentage: Number((po as any).discount_percentage || 0),
          discount_amount: Number((po as any).discount_amount || 0),
          tax_rate: Number((po as any).tax_rate || 0),
          tax_amount: Number(po.tax_amount || 0),
          freight_amount: Number(po.shipping_amount || (po as any).freight_amount || 0), // Use shipping_amount for freight
          other_charges: Number((po as any).other_charges || 0),
          total_amount: Number(po.total_amount || 0),
          payment_terms: po.payment_terms || '',
          payment_method: (po as any).payment_method || '',
          warranty_terms: (po as any).warranty_terms || '',
          retention_percentage: Number((po as any).retention_percentage || 0),
          retention_amount: Number((po as any).retention_amount || 0),
          terms_and_conditions: (po as any).terms_and_conditions || '',
          special_instructions: (po as any).special_instructions || '',
          attachment_ids: (po as any).attachment_ids || []
        });

        console.log('[PurchaseOrderModal] Form data set successfully');
      }
    } catch (error: any) {
      console.error('[PurchaseOrderModal] Load PO error:', error);
      showError({
        title: 'Load Failed',
        message: error?.message || 'Failed to load purchase order'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDuplicateData = (po: PurchaseOrder) => {
    // Map PO data to form, excluding IDs and resetting status fields
    const items: POLineItem[] = (po.items || []).map((item, idx) => ({
      temp_id: `temp_${Date.now()}_${idx}`,
      procurement_item_id: item.item_id,
      item_code: item.item_code,
      item_name: item.item_name,
      description: item.description,
      quantity_ordered: item.quantity_ordered,
      unit_of_measure: item.unit_of_measure,
      unit_price: Number(item.unit_price || 0),
      line_total: Number(item.total_amount || 0),
      required_date: item.requested_delivery_date,
      line_notes: ''
    }));

    setFormData(prev => ({
      ...prev,
      vendor_id: po.vendor_id,
      project_id: po.project_id || '',
      requested_delivery_date: po.requested_delivery_date || po.required_date || '',
      delivery_street: (po as any).ship_to_address?.street || po.delivery_address || '',
      delivery_city: (po as any).ship_to_address?.city || '',
      delivery_state: (po as any).ship_to_address?.state || '',
      delivery_zip: (po as any).ship_to_address?.zip || '',
      delivery_country: (po as any).ship_to_address?.country || 'USA',
      shipping_method: (po.shipping_method as any) || 'ground',
      items,
      subtotal: Number(po.subtotal || 0),
      tax_amount: Number(po.tax_amount || 0),
      shipping_amount: Number(po.shipping_amount || 0),
      total_amount: Number(po.total_amount || 0),
      payment_terms: po.payment_terms || ''
    }));
  };

  const calculateFinancials = useCallback(() => {
    const subtotal = formData.items.reduce((sum, item) => sum + (item.line_total || 0), 0);

    const discountAmount = formData.discount_percentage
      ? (subtotal * formData.discount_percentage) / 100
      : formData.discount_amount || 0;

    const taxableAmount = subtotal - discountAmount;
    const taxAmount = formData.tax_rate ? (taxableAmount * formData.tax_rate) / 100 : 0;

    const total = taxableAmount + taxAmount + (formData.freight_amount || 0) + (formData.other_charges || 0);

    const retentionAmount = formData.retention_percentage
      ? (total * formData.retention_percentage) / 100
      : 0;

    // Only update if values actually changed to avoid infinite loops
    setFormData(prev => {
      if (
        prev.subtotal === subtotal &&
        prev.discount_amount === discountAmount &&
        prev.tax_amount === taxAmount &&
        prev.retention_amount === retentionAmount &&
        prev.total_amount === total
      ) {
        return prev;
      }

      return {
        ...prev,
        subtotal,
        discount_amount: discountAmount,
        tax_amount: taxAmount,
        retention_amount: retentionAmount,
        total_amount: total
      };
    });
  }, [formData.items, formData.discount_percentage, formData.discount_amount, formData.tax_rate, formData.freight_amount, formData.other_charges, formData.retention_percentage]);

  // Calculate financials when items change
  useEffect(() => {
    calculateFinancials();
  }, [calculateFinancials]);

  const addLineItem = (item: ProcurementItem) => {
    const newItem: POLineItem = {
      temp_id: `temp_${Date.now()}`,
      procurement_item_id: item.item_id,
      item_code: item.item_code,
      item_name: item.item_name,
      description: item.description,
      quantity_ordered: 1,
      unit_of_measure: item.unit_of_measure,
      unit_price: item.standard_unit_price || 0,
      line_total: item.standard_unit_price || 0,
      manufacturer: item.manufacturer,
      model_number: item.model_number,
      specifications: item.specifications,
    };

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setSelectedItem(null);
  };

  const updateLineItem = (tempId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map(item => {
        if (item.temp_id !== tempId) return item;

        const updated = { ...item, [field]: value };

        // Recalculate line total
        const qty = updated.quantity_ordered || 0;
        const price = updated.unit_price || 0;
        const discountAmt = updated.discount_amount || 0;
        const taxAmt = updated.tax_amount || 0;

        updated.line_total = (qty * price) - discountAmt + taxAmt;

        return updated;
      })
    }));
  };

  const removeLineItem = (tempId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.temp_id !== tempId)
    }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.vendor_id) newErrors.vendor_id = 'Vendor is required';
        if (!formData.project_id) newErrors.project_id = 'Project is required';
        if (!formData.requested_by) newErrors.requested_by = 'Requested by is required';
        if (!formData.requested_delivery_date) newErrors.requested_delivery_date = 'Delivery date is required';
        break;

      case 3:
        if (formData.items.length === 0) newErrors.items = 'At least one line item is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async (asDraft: boolean = false) => {
    if (!asDraft && !validateStep(currentStep)) {
      return;
    }

    setLoading(true);
    try {
      const userProfile = await authService.getUserProfile();

      // Map line items to backend format
      const lineItems = formData.items.map((item, index) => ({
        line_number: index + 1,
        procurement_item_id: item.procurement_item_id || null,
        item_code: item.item_code,
        item_description: item.item_name,
        specifications: item.description || item.specifications || null,
        quantity_ordered: item.quantity_ordered,
        unit_of_measure: item.unit_of_measure,
        unit_price: item.unit_price,
        required_date: item.required_date || formData.requested_delivery_date,
        delivery_location: formData.delivery_street || null,
        notes: item.line_notes || null
      }));

      const poData: any = {
        po_number: formData.po_number,
        po_type: 'standard',
        priority: formData.priority, // Added priority
        vendor_id: formData.vendor_id,
        vendor_contact_id: selectedContactId || null, // Use selected contact ID
        project_id: formData.project_id,
        department: formData.department || null,
        cost_center: null,
        po_date: formData.po_date,
        required_date: formData.requested_delivery_date,
        delivery_date: null,
        currency: 'USD',
        payment_terms: formData.payment_terms,
        shipping_method: formData.shipping_method || 'ground', // Added shipping_method enum
        shipping_terms: formData.freight_terms || null,
        delivery_terms: formData.delivery_instructions || null,
        partial_delivery: formData.partial_delivery, // Added partial_delivery (corrected field name)
        warranty_terms: formData.warranty_terms || null, // Added warranty_terms
        retention_amount: formData.retention_amount || 0, // Added retention_amount
        terms_and_conditions: formData.terms_and_conditions || null, // Added terms_and_conditions
        tax_amount: formData.tax_amount || 0,
        shipping_amount: formData.freight_amount || 0,
        discount_amount: formData.discount_amount || 0,
        ship_to_address: (formData.delivery_street || formData.delivery_city || formData.delivery_state || formData.delivery_zip) ? {
          street: formData.delivery_street || null,
          city: formData.delivery_city || null,
          state: formData.delivery_state || null,
          zip: formData.delivery_zip || null,
          country: formData.delivery_country || 'USA'
        } : null,
        bill_to_address: null,
        internal_notes: formData.special_instructions || null,
        vendor_instructions: formData.delivery_instructions || null,
        requisition_id: formData.requisition_number || null,
        contract_id: null,
        line_items: lineItems
      };

      let response;
      if (mode === 'edit' && orderId) {
        response = await procurementService.updatePurchaseOrder(orderId, poData);
      } else {
        response = await procurementService.createPurchaseOrder(poData, userProfile.organizationId);
      }

      if (response.success && response.data) {
        const createdPO = response.data;
        const poId = (createdPO as any).po_id || (createdPO as any).purchase_order_id;

        // If not saving as draft, submit for approval
        if (!asDraft && poId && mode !== 'edit') {
          try {
            await procurementService.submitForApproval(poId);
          } catch (submitError: any) {
            console.error('Failed to submit for approval:', submitError);
            // Still show success for creation, but note the submission issue
            showError({
              title: 'PO Created but Submission Failed',
              message: 'The purchase order was created as draft. You can submit it for approval from the list.'
            });
            if (onSuccess) {
              onSuccess(createdPO);
            }
            onOpenChange(false);
            return;
          }
        }

        showSuccess({
          title: asDraft ? 'Draft Saved' : 'PO Submitted',
          message: asDraft
            ? 'Purchase order saved as draft'
            : 'Purchase order created and submitted for approval'
        });

        // Call onSuccess first to refresh data before closing modal
        if (onSuccess) {
          onSuccess(createdPO);
        }
        onOpenChange(false);
      } else {
        showError({
          title: 'Submission Failed',
          message: 'Failed to create purchase order'
        });
      }
    } catch (error: any) {
      showError({
        title: 'Submission Failed',
        message: error?.message || 'An error occurred while creating the purchase order'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVendorChange = async (vendorId: string) => {
    setFormData(prev => ({ ...prev, vendor_id: vendorId }));
    // Load vendor contact info and payment terms
    const vendor = vendors.find(v => v.vendor_id === vendorId);
    if (vendor) {
      setFormData(prev => ({
        ...prev,
        payment_terms: vendor.payment_terms || 'net_30',
        payment_method: prev.payment_method || 'Check',
      }));

      // Load vendor contacts from DB
      try {
        const contactsResponse = await procurementService.getVendorContacts(vendorId);
        if (contactsResponse.success && contactsResponse.data) {
          const contacts = (contactsResponse.data as any).data || contactsResponse.data || [];
          setVendorContacts(Array.isArray(contacts) ? contacts : []);
          // Clear previous contact selection
          setSelectedContactId('');
          setFormData(prev => ({
            ...prev,
            vendor_contact_person: '',
            vendor_contact_email: '',
            vendor_contact_phone: ''
          }));
        }
      } catch (error) {
        console.error('[PurchaseOrderModal] Failed to load vendor contacts:', error);
        setVendorContacts([]);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderDeliveryInfo();
      case 3:
        return renderLineItems();
      case 4:
        return renderFinancialDetails();
      case 5:
        return renderTermsAndAttachments();
      case 6:
        return renderReviewAndSubmit();
      default:
        return null;
    }
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      {formData.po_number && (
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">PO Number</p>
              <p className="text-lg font-semibold">{formData.po_number}</p>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="vendor">
            Vendor <span className="text-destructive">*</span>
          </Label>
          <Select
            value={formData.vendor_id}
            onValueChange={(value) => handleVendorChange(value)}
          >
            <SelectTrigger className={errors.vendor_id ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select vendor" />
            </SelectTrigger>
            <SelectContent>
              {vendors.map(vendor => (
                <SelectItem key={vendor.vendor_id} value={vendor.vendor_id}>
                  {vendor.vendor_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.vendor_id && <p className="text-sm text-destructive">{errors.vendor_id}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="project">
            Project <span className="text-destructive">*</span>
          </Label>
          <Select
            value={formData.project_id}
            onValueChange={(value) => setFormData(prev => ({ ...prev, project_id: value }))}
          >
            <SelectTrigger className={errors.project_id ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map(project => (
                <SelectItem key={project.project_id} value={project.project_id}>
                  {project.project_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.project_id && <p className="text-sm text-destructive">{errors.project_id}</p>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="requested-by">
            Requested By <span className="text-destructive">*</span>
          </Label>
          <Select
            value={formData.requested_by}
            onValueChange={(value) => setFormData(prev => ({ ...prev, requested_by: value }))}
          >
            <SelectTrigger className={errors.requested_by ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select staff" />
            </SelectTrigger>
            <SelectContent>
              {staff.filter(person => person.user_id).map(person => (
                <SelectItem key={person.user_id} value={person.user_id}>
                  {person.first_name} {person.last_name} {person.job_title ? `- ${person.job_title}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.requested_by && <p className="text-sm text-destructive">{errors.requested_by}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="department">Department</Label>
          <Select
            value={formData.department}
            onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Construction">Construction</SelectItem>
              <SelectItem value="Electrical">Electrical</SelectItem>
              <SelectItem value="Plumbing">Plumbing</SelectItem>
              <SelectItem value="HVAC">HVAC</SelectItem>
              <SelectItem value="Safety">Safety</SelectItem>
              <SelectItem value="Quality Control">Quality Control</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="priority">Priority</Label>
          <Select
            value={formData.priority}
            onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="po-date">PO Date</Label>
          <Input
            id="po-date"
            type="date"
            value={formData.po_date}
            onChange={(e) => setFormData(prev => ({ ...prev, po_date: e.target.value }))}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="delivery-date">
            Required Delivery Date <span className="text-destructive">*</span>
          </Label>
          <Input
            id="delivery-date"
            type="date"
            value={formData.requested_delivery_date}
            onChange={(e) => setFormData(prev => ({ ...prev, requested_delivery_date: e.target.value }))}
            className={errors.requested_delivery_date ? 'border-destructive' : ''}
          />
          {errors.requested_delivery_date && (
            <p className="text-sm text-destructive">{errors.requested_delivery_date}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="requisition">Requisition Number</Label>
          <Input
            id="requisition"
            value={formData.requisition_number}
            onChange={(e) => setFormData(prev => ({ ...prev, requisition_number: e.target.value }))}
            placeholder="Optional"
          />
        </div>
      </div>
    </div>
  );

  const renderDeliveryInfo = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Delivery Address</h4>
        <div className="space-y-2">
          <Label htmlFor="delivery-street">Street Address</Label>
          <Input
            id="delivery-street"
            value={formData.delivery_street}
            onChange={(e) => setFormData(prev => ({ ...prev, delivery_street: e.target.value }))}
            placeholder="Street address..."
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="delivery-city">City</Label>
            <Input
              id="delivery-city"
              value={formData.delivery_city}
              onChange={(e) => setFormData(prev => ({ ...prev, delivery_city: e.target.value }))}
              placeholder="City"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="delivery-state">State</Label>
            <Input
              id="delivery-state"
              value={formData.delivery_state}
              onChange={(e) => setFormData(prev => ({ ...prev, delivery_state: e.target.value }))}
              placeholder="State"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="delivery-zip">ZIP Code</Label>
            <Input
              id="delivery-zip"
              value={formData.delivery_zip}
              onChange={(e) => setFormData(prev => ({ ...prev, delivery_zip: e.target.value }))}
              placeholder="ZIP"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="delivery-country">Country</Label>
          <Input
            id="delivery-country"
            value={formData.delivery_country}
            onChange={(e) => setFormData(prev => ({ ...prev, delivery_country: e.target.value }))}
            placeholder="Country"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="shipping-method">Shipping Method</Label>
          <Select
            value={formData.shipping_method}
            onValueChange={(value) => setFormData(prev => ({ ...prev, shipping_method: value as any }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ground">Ground</SelectItem>
              <SelectItem value="express">Express</SelectItem>
              <SelectItem value="overnight">Overnight</SelectItem>
              <SelectItem value="two_day">Two Day</SelectItem>
              <SelectItem value="freight">Freight</SelectItem>
              <SelectItem value="ocean">Ocean</SelectItem>
              <SelectItem value="air">Air</SelectItem>
              <SelectItem value="rail">Rail</SelectItem>
              <SelectItem value="courier">Courier</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="freight-terms">Freight Terms</Label>
          <Select
            value={formData.freight_terms}
            onValueChange={(value) => setFormData(prev => ({ ...prev, freight_terms: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FOB Origin">FOB Origin</SelectItem>
              <SelectItem value="FOB Destination">FOB Destination</SelectItem>
              <SelectItem value="Prepaid">Prepaid</SelectItem>
              <SelectItem value="Collect">Collect</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2 mt-8">
          <Checkbox
            id="partial-delivery"
            checked={formData.partial_delivery}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, partial_delivery: checked as boolean }))}
          />
          <Label htmlFor="partial-delivery" className="text-sm">Allow Partial Delivery</Label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="delivery-instructions">Delivery Instructions</Label>
        <Textarea
          id="delivery-instructions"
          value={formData.delivery_instructions}
          onChange={(e) => setFormData(prev => ({ ...prev, delivery_instructions: e.target.value }))}
          placeholder="Special delivery instructions, access codes, etc..."
          rows={3}
        />
      </div>

      <Separator />

      <div className="space-y-4">
        <h4 className="font-medium text-sm">Vendor Contact Information</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vendor-contact">Select Contact</Label>
            <Select
              value={selectedContactId}
              onValueChange={(value) => {
                setSelectedContactId(value);
                const contact = vendorContacts.find(c => c.contact_id === value);
                if (contact) {
                  setFormData(prev => ({
                    ...prev,
                    vendor_contact_person: contact.contact_name || '',
                    vendor_contact_email: contact.email || '',
                    vendor_contact_phone: contact.phone || ''
                  }));
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select contact..." />
              </SelectTrigger>
              <SelectContent>
                {vendorContacts.map(contact => (
                  <SelectItem key={contact.contact_id} value={contact.contact_id}>
                    {contact.contact_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              type="email"
              value={formData.vendor_contact_email}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-phone">Phone</Label>
            <Input
              id="contact-phone"
              type="tel"
              value={formData.vendor_contact_phone}
              readOnly
              className="bg-muted"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderLineItems = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium">Order Items</h4>
          <p className="text-sm text-muted-foreground">{formData.items.length} items</p>
        </div>
        <ItemSearchSelect
          value={selectedItem}
          onChange={(item) => {
            if (item) {
              addLineItem(item);
            }
          }}
          onAddCustomItem={() => setShowAddItemModal(true)}
          vendorId={formData.vendor_id}
          className="w-[400px]"
        />
      </div>

      {errors.items && (
        <div className="flex items-center gap-2 p-3 border border-destructive bg-destructive/10 rounded-lg">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{errors.items}</p>
        </div>
      )}

      {formData.items.length > 0 && (
        <div className="border rounded-lg">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Item</TableHead>
                  <TableHead className="w-[100px]">Qty</TableHead>
                  <TableHead className="w-[80px]">UOM</TableHead>
                  <TableHead className="w-[120px]">Unit Price</TableHead>
                  <TableHead className="w-[120px]">Line Total</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.items.map((item) => (
                  <TableRow key={item.temp_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{item.item_code}</p>
                        <p className="text-sm text-muted-foreground">{item.item_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.quantity_ordered}
                        onChange={(e) => updateLineItem(item.temp_id, 'quantity_ordered', parseFloat(e.target.value) || 0)}
                        className="w-20"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{item.unit_of_measure}</span>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(item.temp_id, 'unit_price', parseFloat(e.target.value) || 0)}
                        className="w-28"
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">${formatCurrency(item.line_total)}</span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(item.temp_id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      )}

      <AddCustomItemModal
        open={showAddItemModal}
        onOpenChange={setShowAddItemModal}
        onItemCreated={(item) => {
          addLineItem(item);
          setShowAddItemModal(false);
        }}
      />
    </div>
  );

  const renderFinancialDetails = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Financial Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium">${formatCurrency(formData.subtotal)}</span>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="discount-pct">Discount %</Label>
              <Input
                id="discount-pct"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.discount_percentage || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  discount_percentage: e.target.value ? parseFloat(e.target.value) : 0
                }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <div className="flex items-center h-10 px-3 border rounded-md bg-muted">
                <span className="text-sm">${formatCurrency(formData.discount_amount)}</span>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tax-rate">Tax Rate %</Label>
              <Input
                id="tax-rate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={formData.tax_rate || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  tax_rate: e.target.value ? parseFloat(e.target.value) : 0
                }))}
              />
            </div>

            <div className="space-y-2">
              <Label>Tax Amount</Label>
              <div className="flex items-center h-10 px-3 border rounded-md bg-muted">
                <span className="text-sm">${formatCurrency(formData.tax_amount)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="freight">Freight/Shipping</Label>
              <Input
                id="freight"
                type="number"
                min="0"
                step="0.01"
                value={formData.freight_amount || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  freight_amount: e.target.value ? parseFloat(e.target.value) : 0
                }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="other-charges">Other Charges</Label>
              <Input
                id="other-charges"
                type="number"
                min="0"
                step="0.01"
                value={formData.other_charges || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  other_charges: e.target.value ? parseFloat(e.target.value) : 0
                }))}
              />
            </div>
          </div>

          <Separator />

          <div className="flex justify-between items-center text-lg font-bold">
            <span>Total Amount</span>
            <span className="text-primary">${formatCurrency(formData.total_amount)}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="payment-terms">Payment Terms</Label>
          <Select
            value={formData.payment_terms}
            onValueChange={(value) => setFormData(prev => ({ ...prev, payment_terms: value }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cod">Cash on Delivery</SelectItem>
              <SelectItem value="net_15">Net 15 Days</SelectItem>
              <SelectItem value="net_30">Net 30 Days</SelectItem>
              <SelectItem value="net_45">Net 45 Days</SelectItem>
              <SelectItem value="net_60">Net 60 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="payment-method">Payment Method</Label>
          <Select
            value={formData.payment_method}
            onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select method" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Check">Check</SelectItem>
              <SelectItem value="ACH Transfer">ACH Transfer</SelectItem>
              <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
              <SelectItem value="Credit Card">Credit Card</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  const renderTermsAndAttachments = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="warranty">Warranty Terms</Label>
        <Textarea
          id="warranty"
          value={formData.warranty_terms}
          onChange={(e) => setFormData(prev => ({ ...prev, warranty_terms: e.target.value }))}
          placeholder="Warranty terms and conditions..."
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="retention-pct">Retention %</Label>
          <Input
            id="retention-pct"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={formData.retention_percentage || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              retention_percentage: e.target.value ? parseFloat(e.target.value) : 0
            }))}
          />
        </div>

        <div className="space-y-2">
          <Label>Retention Amount</Label>
          <div className="flex items-center h-10 px-3 border rounded-md bg-muted">
            <span className="text-sm">${formatCurrency(formData.retention_amount)}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="terms">Terms & Conditions</Label>
        <Textarea
          id="terms"
          value={formData.terms_and_conditions}
          onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
          placeholder="Standard terms and conditions..."
          rows={5}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="instructions">Special Instructions</Label>
        <Textarea
          id="instructions"
          value={formData.special_instructions}
          onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
          placeholder="Any special instructions for this order..."
          rows={3}
        />
      </div>
    </div>
  );

  const renderReviewAndSubmit = () => {
    const selectedVendor = vendors.find(v => v.vendor_id === formData.vendor_id);
    const selectedProject = projects.find(p => p.project_id === formData.project_id);
    const selectedStaff = staff.find(s => s.user_id === formData.requested_by);

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Purchase Order Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Vendor</p>
                <p className="font-medium">{selectedVendor?.vendor_name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Project</p>
                <p className="font-medium">{selectedProject?.project_name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Requested By</p>
                <p className="font-medium">
                  {selectedStaff ? `${selectedStaff.first_name} ${selectedStaff.last_name}` : formData.requested_by}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Delivery Date</p>
                <p className="font-medium">{formData.requested_delivery_date}</p>
              </div>
            </div>

            <Separator />

            <div>
              <p className="text-sm text-muted-foreground mb-2">Line Items ({formData.items.length})</p>
              <div className="space-y-2">
                {formData.items.map((item) => (
                  <div key={item.temp_id} className="flex justify-between text-sm">
                    <span>{item.item_name}</span>
                    <span className="font-medium">${formatCurrency(item.line_total)}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${formatCurrency(formData.subtotal)}</span>
              </div>
              {formData.discount_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Discount</span>
                  <span>-${formatCurrency(formData.discount_amount)}</span>
                </div>
              )}
              {formData.tax_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>${formatCurrency(formData.tax_amount)}</span>
                </div>
              )}
              {formData.freight_amount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Freight</span>
                  <span>${formatCurrency(formData.freight_amount)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-primary">${formatCurrency(formData.total_amount)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {budgetWarnings.length > 0 && (
          <Card className="border-amber-500">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Budget Warnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1">
                {budgetWarnings.map((warning, idx) => (
                  <li key={idx} className="text-sm text-amber-600">{warning}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const getModalTitle = () => {
    if (mode === 'duplicate') return 'Duplicate Purchase Order';
    if (mode === 'edit') return 'Edit Purchase Order';
    return 'Create Purchase Order';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1200px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {getModalTitle()}
          </DialogTitle>
          <DialogDescription>
            Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1].title}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between px-4 py-2 bg-muted/50 rounded-lg">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;

            return (
              <div key={step.id} className="flex items-center flex-1">
                <div
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-md transition-colors',
                    isActive && 'bg-primary text-primary-foreground',
                    isCompleted && 'bg-primary/20 text-primary',
                    !isActive && !isCompleted && 'text-muted-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-medium hidden sm:inline">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={cn('flex-1 h-0.5 mx-2', isCompleted ? 'bg-primary' : 'bg-muted')} />
                )}
              </div>
            );
          })}
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 px-6">
          {renderStepContent()}
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2">
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack} disabled={loading}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {currentStep === STEPS.length && (
                <Button variant="outline" onClick={() => handleSubmit(true)} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  Save as Draft
                </Button>
              )}

              {currentStep < STEPS.length ? (
                <Button onClick={handleNext} disabled={loading}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={() => handleSubmit(false)} disabled={loading}>
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Submitting...' : 'Submit for Approval'}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
